/**
 * SUNTRONIX ONE — ONE CONNECT hub.
 * The authorised gateway between SUNTRONIX ONE and every external system.
 * External data is EVIDENCE, never automatic truth.
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  PlusCircle, PlayCircle, Loader2, ShieldCheck, Zap, Cable,
  AlertCircle, TrendingUp, RefreshCcw, Link2, Ban, Sparkles,
} from "lucide-react";

const STATE_STYLE = {
  HEALTHY:                 { bg: "#ECFDF6", fg: "#169C70" },
  CONNECTED:               { bg: "#EFF5FF", fg: "#246BFD" },
  DEGRADED:                { bg: "#FFF8E7", fg: "#B25A00" },
  PARTIAL_FAILURE:         { bg: "#FFF8E7", fg: "#B25A00" },
  RATE_LIMITED:            { bg: "#FFF8E7", fg: "#B25A00" },
  PERMISSION_ISSUE:        { bg: "#FFF0F2", fg: "#C2333F" },
  TOKEN_EXPIRING:          { bg: "#FFF8E7", fg: "#B25A00" },
  AUTHENTICATION_EXPIRED:  { bg: "#FFF0F2", fg: "#C2333F" },
  AUTHENTICATION_REQUIRED: { bg: "#FFF8E7", fg: "#B25A00" },
  SYNC_FAILED:             { bg: "#FFF0F2", fg: "#C2333F" },
  SUSPENDED:               { bg: "#F2F4F7", fg: "#667085" },
  DISABLED:                { bg: "#F2F4F7", fg: "#667085" },
  REVIEW_REQUIRED:         { bg: "#FFF8E7", fg: "#B25A00" },
  NOT_CONFIGURED:          { bg: "#F2F4F7", fg: "#667085" },
  CONFIGURATION_IN_PROGRESS:{ bg: "#EFF5FF", fg: "#246BFD" },
};
const CATEGORY_LABEL = {
  IDENTITY_AND_DEVICE:  "Identity & device management",
  HR_AND_EMPLOYEE:      "HR & employee",
  LOGISTICS:            "Logistics",
  FINANCE_AND_TAX:      "Finance & tax",
  BUSINESS_SYSTEMS:     "Business systems",
  CLOUD_AND_DISCOVERY:  "Cloud & discovery",
  SECURITY:             "Security",
  LEGAL:                "Legal",
  COMMUNICATION:        "Communication",
  ITAD_SUSTAINABILITY:  "ITAD / sustainability",
};

const Pill = ({ style, children }) => (
  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
        style={{ background: style.bg, color: style.fg }}>{children}</span>
);

export default function OneConnect() {
  const [catalogue, setCatalogue] = useState(null);
  const [connections, setConnections] = useState([]);
  const [health, setHealth] = useState(null);
  const [masters, setMasters] = useState([]);
  const [reconItems, setReconItems] = useState([]);
  const [reconSummary, setReconSummary] = useState({open:0,linked:0,created:0,rejected:0});
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [newFor, setNewFor] = useState(null);
  const [resolveFor, setResolveFor] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [cat, conn, h, m, r, rs] = await Promise.all([
        api.get("/one-connect/catalogue"),
        api.get("/one-connect/connections"),
        api.get("/one-connect/health"),
        api.get("/master-companies"),
        api.get("/one-connect/reconciliation?state=OPEN"),
        api.get("/one-connect/reconciliation/summary"),
      ]);
      setCatalogue(cat.data);
      setConnections(conn.data.items || []);
      setHealth(h.data);
      setMasters(m.data.items || []);
      setReconItems(r.data.items || []);
      setReconSummary(rs.data || {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const test = async (id) => {
    try {
      const { data } = await api.post(`/one-connect/connections/${id}/test`);
      toast.success(`Connection → ${data.state.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runSync = async (id, connectorId) => {
    if (!["microsoft-intune","apple-business","google-workspace"].includes(connectorId)) {
      toast.error(`Sync engine not yet available for ${connectorId}`);
      return;
    }
    setSyncing(id);
    try {
      const { data } = await api.post(`/one-connect/connections/${id}/sync`);
      toast.success(
        `Sync complete · read ${data.records_read} · linked ${data.records_mapped + data.records_evidence} · queued ${data.records_queued}`
      );
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSyncing(null); }
  };

  const byCategory = (catalogue?.connectors || []).reduce((acc, c) => {
    (acc[c.category] = acc[c.category] || []).push(c); return acc;
  }, {});
  const connByConnector = connections.reduce((acc, c) => {
    (acc[c.connector_id] = acc[c.connector_id] || []).push(c); return acc;
  }, {});

  return (
    <div className="max-w-[1400px] space-y-4" data-testid="one-connect-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-[#667085]">
          Administration · Integrations
        </div>
        <h1 className="text-2xl font-heading font-semibold text-[#101828] tracking-tight">
          ONE CONNECT
        </h1>
        <p className="text-[13px] text-[#667085] mt-1 max-w-3xl">
          The authorised gateway between SUNTRONIX ONE and every external
          system.  External data is <b>evidence</b> — never automatic truth.
          Golden Asset Record remains authoritative.
        </p>
      </div>

      {/* Health strip */}
      {health && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="oc-health">
          <Kpi tone="mint"  label="Healthy"     value={health.healthy}      icon={<Cable className="h-4 w-4" />} />
          <Kpi tone="amber" label="Degraded"    value={health.degraded}     icon={<TrendingUp className="h-4 w-4" />} />
          <Kpi tone="amber" label="Auth req."   value={health.authentication_required} icon={<ShieldCheck className="h-4 w-4" />} />
          <Kpi tone="coral" label="Failed"      value={health.failed}       icon={<AlertCircle className="h-4 w-4" />} />
          <Kpi tone="amber" label="Rate limit"  value={health.rate_limited} icon={<Zap className="h-4 w-4" />} />
          <Kpi tone="blue"  label="Total"       value={health.total_connections} icon={<Cable className="h-4 w-4" />} />
        </div>
      )}

      <Tabs defaultValue="connected" data-testid="oc-tabs">
        <TabsList>
          <TabsTrigger value="connected" data-testid="oc-tab-connected">
            Connected systems ({connections.length})
          </TabsTrigger>
          <TabsTrigger value="available" data-testid="oc-tab-available">
            Available connectors ({catalogue?.connectors?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="reconciliation" data-testid="oc-tab-recon">
            Reconciliation queue
            {reconSummary.open > 0 && (
              <span className="ml-2 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-[#FFF0F2] text-[10px] font-semibold text-[#C2333F]">
                {reconSummary.open}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Connected */}
        <TabsContent value="connected" className="mt-3">
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase text-[#667085]">
                <tr>
                  <th className="text-left px-3 py-2">Connector</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Env</th>
                  <th className="text-left px-3 py-2">State</th>
                  <th className="text-left px-3 py-2">Last sync</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {busy && connections.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-[#98A2B3]">
                    <Loader2 className="h-4 w-4 mx-auto animate-spin" />
                  </td></tr>
                ) : connections.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-[#98A2B3] text-[13px]">
                    <Cable className="h-5 w-5 mx-auto mb-2 opacity-50" />
                    No connections yet. Pick a connector from <b>Available connectors</b> to begin.
                  </td></tr>
                ) : connections.map((c) => {
                  const st = STATE_STYLE[c.state] || STATE_STYLE.NOT_CONFIGURED;
                  return (
                    <tr key={c.id} className="hover:bg-slate-50"
                        data-testid={`oc-conn-${c.id}`}>
                      <td className="px-3 py-2">
                        <div className="text-[13px] text-[#101828] font-medium">{c.display_name}</div>
                        <div className="text-[11px] text-[#667085]">{c.provider} · {c.connector_id}</div>
                      </td>
                      <td className="px-3 py-2 text-[12px] text-[#667085]">
                        {masters.find((m) => m.id === c.master_company_id)?.name || c.master_company_id?.slice(0, 8)}
                      </td>
                      <td className="px-3 py-2 text-[11px] font-mono text-[#667085]">{c.environment}</td>
                      <td className="px-3 py-2"><Pill style={st}>{c.state.replace(/_/g, " ")}</Pill></td>
                      <td className="px-3 py-2 text-[11px] text-[#667085]">
                        {c.last_sync_at ? new Date(c.last_sync_at).toLocaleString() : "Never"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => test(c.id)}
                                  data-testid={`oc-test-${c.id}`}>
                            <PlayCircle className="h-3 w-3 mr-1" /> Test
                          </Button>
                          <Button size="sm" onClick={() => runSync(c.id, c.connector_id)}
                                  disabled={syncing === c.id}
                                  className="bg-brand-blue hover:bg-brand-blueh text-white"
                                  data-testid={`oc-sync-${c.id}`}>
                            {syncing === c.id
                              ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              : <RefreshCcw className="h-3 w-3 mr-1" />}
                            Sync
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Available */}
        <TabsContent value="available" className="mt-3 space-y-4">
          {(catalogue?.categories || []).map((cat) => {
            const list = byCategory[cat] || [];
            if (!list.length) return null;
            return (
              <div key={cat} data-testid={`oc-cat-${cat}`}>
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#667085] mb-2">
                  {CATEGORY_LABEL[cat] || cat}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {list.map((c) => {
                    const existing = connByConnector[c.id] || [];
                    return (
                      <div key={c.id}
                           className="bg-white border border-slate-200 rounded-md p-4 hover:border-slate-400 transition-colors"
                           data-testid={`oc-connector-${c.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-medium text-[#101828]">{c.name}</div>
                            <div className="text-[11px] text-[#667085]">{c.provider}</div>
                          </div>
                          {existing.length > 0 && (
                            <Pill style={STATE_STYLE.HEALTHY}>{existing.length} live</Pill>
                          )}
                        </div>
                        <p className="text-[12px] text-[#667085] mt-2 leading-relaxed">{c.description}</p>
                        <div className="mt-3 flex flex-wrap gap-1">
                          <span className="text-[10px] rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[#667085]">
                            {c.direction}
                          </span>
                          <span className="text-[10px] rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[#667085]">
                            {c.auth}
                          </span>
                          {c.webhook && (
                            <span className="text-[10px] rounded bg-[#ECFDF6] px-1.5 py-0.5 text-[#169C70]">
                              webhook
                            </span>
                          )}
                        </div>
                        <Button size="sm" onClick={() => setNewFor(c)}
                                data-testid={`oc-connect-${c.id}`}
                                className="mt-3 w-full bg-brand-blue hover:bg-brand-blueh">
                          <PlusCircle className="h-3 w-3 mr-1" /> Connect
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </TabsContent>

        {/* Reconciliation queue */}
        <TabsContent value="reconciliation" className="mt-3" data-testid="oc-recon-tab-panel">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
            <MiniStat label="Open"     value={reconSummary.open}     tone="coral" />
            <MiniStat label="In review" value={reconSummary.in_review} tone="amber" />
            <MiniStat label="Linked"   value={reconSummary.linked}   tone="mint"  />
            <MiniStat label="Created"  value={reconSummary.created}  tone="blue"  />
            <MiniStat label="Rejected" value={reconSummary.rejected} tone="mint"  />
          </div>
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase text-[#667085]">
                <tr>
                  <th className="text-left px-3 py-2">External device</th>
                  <th className="text-left px-3 py-2">Serial</th>
                  <th className="text-left px-3 py-2">Model / OS</th>
                  <th className="text-left px-3 py-2">Assignee</th>
                  <th className="text-left px-3 py-2">Seen</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {reconItems.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-[#98A2B3] text-[13px]">
                    <ShieldCheck className="h-5 w-5 mx-auto mb-2 opacity-50 text-[#169C70]" />
                    Golden Asset Record is in sync — nothing to review.
                  </td></tr>
                ) : reconItems.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`oc-recon-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="text-[12px] font-mono text-[#101828]">{r.external_id}</div>
                      <div className="text-[11px] text-[#667085]">{r.connector_id} · seen {r.occurrences}×</div>
                    </td>
                    <td className="px-3 py-2 text-[12px] font-mono text-[#101828]">{r.serial || "—"}</td>
                    <td className="px-3 py-2 text-[12px] text-[#667085]">
                      <div className="text-[#101828]">{r.model || "—"}</div>
                      <div className="text-[11px]">{r.os || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-[12px] text-[#667085]">{r.assignee || "—"}</td>
                    <td className="px-3 py-2 text-[11px] text-[#667085]">
                      {r.last_seen_at ? new Date(r.last_seen_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => setResolveFor(r)}
                              data-testid={`oc-recon-resolve-${r.id}`}>
                        <Sparkles className="h-3 w-3 mr-1" /> Resolve
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <ConnectDialog connector={newFor} masters={masters}
                       onClose={() => setNewFor(null)} onSaved={load} />
      <ResolveDialog item={resolveFor} masters={masters}
                       onClose={() => setResolveFor(null)} onDone={load} />
    </div>
  );
}

function Kpi({ label, value, tone, icon }) {
  const map = {
    blue:  { bg: "#EFF5FF", accent: "#246BFD" },
    coral: { bg: "#FFF0F2", accent: "#C2333F" },
    amber: { bg: "#FFF8E7", accent: "#B25A00" },
    mint:  { bg: "#ECFDF6", accent: "#169C70" },
  };
  const t = map[tone] || map.blue;
  return (
    <div className="rounded-md border border-slate-200 p-3 relative overflow-hidden"
         style={{ background: `linear-gradient(180deg, ${t.bg} 0%, #FFFFFF 60%)` }}>
      <div className="absolute inset-y-0 left-0 w-1" style={{ background: t.accent }} />
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">{label}</div>
        <span style={{ color: t.accent }}>{icon}</span>
      </div>
      <div className="mt-1 text-2xl font-semibold text-[#101828]">{value ?? 0}</div>
    </div>
  );
}

function ConnectDialog({ connector, masters, onClose, onSaved }) {
  const [mc, setMc] = useState("");
  const [env, setEnv] = useState("PRODUCTION");
  const [externalTenant, setExternalTenant] = useState("");
  const [secret, setSecret] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (connector) { setMc(""); setEnv("PRODUCTION"); setExternalTenant(""); setSecret(""); }
  }, [connector]);
  if (!connector) return null;

  const save = async () => {
    if (!mc) { toast.error("Pick a customer"); return; }
    setSaving(true);
    try {
      await api.post("/one-connect/connections", {
        connector_id: connector.id,
        master_company_id: mc,
        environment: env,
        external_tenant_id: externalTenant || undefined,
        secret_payload: secret ? { primary: secret } : undefined,
      });
      toast.success(`${connector.name} connection created`);
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!connector} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="oc-new-dialog">
        <DialogHeader><DialogTitle>Connect {connector.name}</DialogTitle></DialogHeader>
        <div className="text-[12px] text-[#667085]">{connector.description}</div>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">Customer</label>
            <select value={mc} onChange={(e) => setMc(e.target.value)}
                    data-testid="oc-new-master"
                    className="w-full h-9 border border-slate-200 rounded px-2 text-[13px] mt-1 bg-white">
              <option value="">— select —</option>
              {masters.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">Environment</label>
            <select value={env} onChange={(e) => setEnv(e.target.value)}
                    data-testid="oc-new-env"
                    className="w-full h-9 border border-slate-200 rounded px-2 text-[13px] mt-1 bg-white">
              {["SANDBOX", "UAT", "PRODUCTION"].map((e) => <option key={e}>{e}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">
              External tenant ID (optional)
            </label>
            <Input value={externalTenant} onChange={(e) => setExternalTenant(e.target.value)}
                    data-testid="oc-new-external"
                    placeholder="e.g. Azure tenant id, AWS account, DEP org" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">
              Secret / API key ({connector.auth})
            </label>
            <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)}
                    data-testid="oc-new-secret"
                    placeholder="Stored server-side · never displayed again" />
            <div className="text-[11px] text-[#667085] mt-1 flex items-center gap-1">
              <ShieldCheck className="h-3 w-3 text-brand-mint" />
              Secret is stored separately and never returned by any API.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="create" onClick={save} disabled={saving}
                    className="bg-brand-blue hover:bg-brand-blueh"
                    data-testid="oc-new-save">
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Create connection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ label, value, tone }) {
  const map = {
    blue:  { bg: "#EFF5FF", accent: "#246BFD" },
    coral: { bg: "#FFF0F2", accent: "#C2333F" },
    amber: { bg: "#FFF8E7", accent: "#B25A00" },
    mint:  { bg: "#ECFDF6", accent: "#169C70" },
  };
  const t = map[tone] || map.blue;
  return (
    <div className="rounded border border-slate-200 px-3 py-2"
           style={{ background: t.bg }}>
      <div className="text-[10px] uppercase tracking-[0.12em]"
              style={{ color: t.accent }}>{label}</div>
      <div className="text-lg font-semibold text-[#101828]">{value ?? 0}</div>
    </div>
  );
}

function ResolveDialog({ item, masters, onClose, onDone }) {
  const [action, setAction] = useState("create_asset");
  const [assetId, setAssetId] = useState("");
  const [category, setCategory] = useState("Laptop");
  const [reason, setReason] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item) {
      setAction("create_asset"); setAssetId(""); setReason("");
      setCategory("Laptop");
      // Prefetch some candidates from the same tenant so the user can
      // link the evidence to an existing asset if serial is a near-miss.
      api.get(`/assets/?master_company_id=${item.master_company_id}&limit=25`)
        .then((r) => setCandidates(r.data.items || []))
        .catch(() => setCandidates([]));
    }
  }, [item]);
  if (!item) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const body = { action };
      if (action === "link_asset") {
        if (!assetId) { toast.error("Pick an asset to link"); setBusy(false); return; }
        body.asset_id = assetId;
      } else if (action === "reject") {
        body.reason = reason || "Rejected by operator";
      } else if (action === "create_asset") {
        body.category = category;
      }
      const { data } = await api.post(
        `/one-connect/reconciliation/${item.id}/resolve`, body);
      toast.success(`Reconciliation ${data.state.toLowerCase()}`);
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="oc-recon-dialog">
        <DialogHeader>
          <DialogTitle>Resolve external device</DialogTitle>
        </DialogHeader>
        <div className="rounded border border-slate-200 bg-slate-50 p-3 text-[12px] space-y-1">
          <div className="text-[#667085]">
            <span className="uppercase tracking-[0.12em] text-[10px]">Connector</span>
            <div className="font-mono text-[#101828]">{item.connector_id}</div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-1">
            <div>
              <div className="uppercase tracking-[0.12em] text-[10px] text-[#667085]">Serial</div>
              <div className="font-mono text-[#101828]">{item.serial || "—"}</div>
            </div>
            <div>
              <div className="uppercase tracking-[0.12em] text-[10px] text-[#667085]">Model</div>
              <div className="text-[#101828]">{item.model || "—"}</div>
            </div>
          </div>
        </div>

        <div className="mt-3 space-y-2">
          <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">Action</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "create_asset", label: "Create asset", i: <Sparkles className="h-3.5 w-3.5" /> },
              { v: "link_asset",   label: "Link existing", i: <Link2 className="h-3.5 w-3.5" /> },
              { v: "reject",       label: "Reject",        i: <Ban className="h-3.5 w-3.5" /> },
            ].map((o) => (
              <button key={o.v} onClick={() => setAction(o.v)}
                        data-testid={`oc-recon-action-${o.v}`}
                        className={`border rounded px-2 py-1.5 text-[12px] flex items-center justify-center gap-1 transition-colors ${
                            action === o.v ? "border-[#246BFD] bg-[#EFF5FF] text-[#246BFD]"
                                                : "border-slate-200 text-[#667085] hover:bg-slate-50"}`}>
                {o.i} {o.label}
              </button>
            ))}
          </div>

          {action === "create_asset" && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">Category</label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)}
                      data-testid="oc-recon-category" />
              <div className="text-[11px] text-[#667085] mt-1">
                A new Golden Asset Record will be created and linked to this external device.
              </div>
            </div>
          )}
          {action === "link_asset" && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">
                Pick existing asset in tenant
              </label>
              <select value={assetId} onChange={(e) => setAssetId(e.target.value)}
                        data-testid="oc-recon-asset"
                        className="w-full h-9 border border-slate-200 rounded px-2 text-[13px] mt-1 bg-white">
                <option value="">— pick asset —</option>
                {candidates.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.asset_tag || a.serial} · {a.model || a.category || "—"}
                  </option>
                ))}
              </select>
            </div>
          )}
          {action === "reject" && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">Reason</label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)}
                      data-testid="oc-recon-reason"
                      placeholder="e.g. Test device / decommissioned / duplicate" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
                    data-testid="oc-recon-submit"
                    className="bg-brand-blue hover:bg-brand-blueh">
            {busy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
